import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import BankScopeField from '../../components/BankScopeField';
import ScopeField from '../../components/ScopeField';
import LotteryCodeField from '../../components/LotteryCodeField';
import { deleteProductConfig, listProductConfigs } from '../../api/products';
import './ParametresLoteriePage.css';
import './EliminarParametrosPage.css';

// Surcharges groupe/banque : apres suppression, la vente retombe sur le niveau
// au-dessus (bank -> group -> company, cf. products/services.py:resolve_config_in_chain).
// Le niveau "General" (company) est permis depuis 2026-09-27 avec un avertissement.
// "Superviseur" = configs de SES groupes + de toutes les banks qu'il couvre
// (ProductConfig n'a pas de portee superviseur propre, cf. ?supervisor= backend).
// "General" (2026-09-27, demande utilisateur) : parametres de la company elle-meme
// (ni groupe ni banque) — sert surtout a supprimer le TOUS general. Aucune
// banque/groupe a choisir : l'API sans ?group=/?bank= renvoie ce niveau-la.
const FILTERS = ['General', 'Groupe', 'Superviseur', 'Banque'];

const scopeParams = (filter, scopeId) => {
  if (filter === 'General') return {};
  if (filter === 'Groupe') return { group: scopeId };
  if (filter === 'Superviseur') return { supervisor: scopeId };
  return { bank: scopeId };
};

// Loterie : toutes les loteries, seulement les parametres TOUS (tirage vide =
// parametre general, cf. products/views.py:_filter_config_tirage '0'), ou une
// loterie precise (2026-09-27).
const LOTTERY_MODES = [
  { key: 'all', label: 'Toutes' },
  { key: 'tous', label: 'TOUS seulement' },
  { key: 'one', label: 'Une loterie' },
];

const scopeLabel = (c) => {
  if (c.bank) return `Banque ${c.bank_name || ''}${c.bank_username ? ` (${c.bank_username})` : ''}`;
  if (c.group) return `Groupe ${c.group_name || ''}`;
  return 'General (company)';
};

const EliminarParametrosPage = () => {
  const [filter, setFilter] = useState('Banque');
  const [scopeId, setScopeId] = useState('');
  const [scopeName, setScopeName] = useState('');
  const [lotteryMode, setLotteryMode] = useState('all');
  const [lottery, setLottery] = useState('');
  const [lotteryName, setLotteryName] = useState('');

  const [configs, setConfigs] = useState(null); // null = pas encore cherche
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletedMsg, setDeletedMsg] = useState('');

  const handleFilterChange = (opt) => {
    setFilter(opt);
    setScopeId('');
    setScopeName('');
    setConfigs(null);
    setSelected([]);
    setConfirming(false);
  };

  const load = async () => {
    setLoading(true);
    setError('');
    setSelected([]);
    setConfirming(false);
    try {
      const params = { ...scopeParams(filter, scopeId) };
      if (lotteryMode === 'tous') params.tirage = '0';
      else if (lotteryMode === 'one') params.tirage = lottery;
      const data = await listProductConfigs(params);
      data.sort((a, b) => (a.tirage - b.tirage) || scopeLabel(a).localeCompare(scopeLabel(b))
        || String(a.product_abbr).localeCompare(String(b.product_abbr)));
      setConfigs(data);
    } catch (err) {
      setError('Impossible de charger les parametres.');
      setConfigs(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setDeletedMsg('');
    if (filter !== 'General' && !scopeId) {
      setError(`Choisis d'abord un(e) ${filter.toLowerCase()}.`);
      return;
    }
    if (lotteryMode === 'one' && (!lottery || String(lottery) === '0')) {
      setError('Choisis la loterie.');
      return;
    }
    load();
  };

  const toggle = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    setConfirming(false);
  };

  const allChecked = configs && configs.length > 0 && selected.length === configs.length;
  const toggleAll = () => {
    setSelected(allChecked ? [] : configs.map((c) => c.id));
    setConfirming(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError('');
    let done = 0;
    try {
      // En serie : message clair sur la premiere suppression qui echoue.
      for (const id of selected) {
        await deleteProductConfig(id);
        done += 1;
      }
      setDeletedMsg(`${done} parametre(s) supprime(s).`);
    } catch (err) {
      setError(`Suppression interrompue apres ${done} parametre(s) — reessaie.`);
    } finally {
      setDeleting(false);
      setConfirming(false);
      await load();
    }
  };

  return (
    <Layout>
      <p className="pl-breadcrumb">
        Ruta : <Link to="/dashboard" className="bc-link">Menu</Link>
        {' >> '}
        <Link to="/controles" className="bc-link">Controles</Link>
        {' >> '}
        <span className="bc-current">Eliminar Parametros</span>
      </p>

      <div className="pl-card">
        <div className="pl-card-header">
          <span className="pl-card-title">Eliminar Parametros</span>
        </div>
        <div className="pl-card-body">
          <div className="pl-radio-row">
            {FILTERS.map((opt) => (
              <label key={opt} className="pl-radio-label">
                <input
                  type="radio"
                  name="ep-filter"
                  value={opt}
                  checked={filter === opt}
                  onChange={() => handleFilterChange(opt)}
                />
                {opt === 'General' ? 'General (company)' : opt}
              </label>
            ))}
          </div>

          {filter !== 'General' && (
          <div className="pl-row pl-scope-row">
            <label className="pl-label">{filter}</label>
            {filter === 'Banque' ? (
              <BankScopeField
                id={scopeId}
                name={scopeName}
                onResolve={(id, name) => { setScopeId(id); setScopeName(name); setConfigs(null); }}
                inputClass="pl-input pl-input-sm"
                iconClass="pl-icon-btn"
                nameClass="pl-scope-name"
                errClass="pl-lookup-err"
              />
            ) : (
              <ScopeField
                filter={filter}
                id={scopeId}
                name={scopeName}
                onResolve={(id, nm) => { setScopeId(id); setScopeName(nm); setConfigs(null); }}
                inputClass="pl-input pl-input-sm"
                iconClass="pl-icon-btn"
                nameClass="pl-scope-name"
                errClass="pl-lookup-err"
              />
            )}
          </div>
          )}

          <form onSubmit={handleSearch} className="pl-form">
            <div className="pl-row">
              <label className="pl-label">Loterie</label>
              <div className="pl-radio-row ep-lottery-modes">
                {LOTTERY_MODES.map((m) => (
                  <label key={m.key} className="pl-radio-label">
                    <input
                      type="radio"
                      name="ep-lottery-mode"
                      checked={lotteryMode === m.key}
                      onChange={() => { setLotteryMode(m.key); setConfigs(null); setSelected([]); }}
                    />
                    {m.label}
                  </label>
                ))}
              </div>
            </div>

            {lotteryMode === 'one' && (
            <div className="pl-row">
              <label className="pl-label" />
              <LotteryCodeField
                value={lottery}
                name={lotteryName}
                onChange={(code) => { setLottery(code); setLotteryName(''); }}
                onResolve={(code, nm) => { setLottery(code); setLotteryName(nm); }}
                inputClass="pl-input pl-input-sm"
                iconClass="pl-icon-btn"
                nameClass="pl-scope-name"
                errClass="pl-lookup-err"
              />
            </div>
            )}

            {lotteryMode === 'tous' && (
              <p className="ep-hint">
                TOUS = parametre general du niveau choisi, utilise pour les loteries qui n&apos;ont pas
                leur propre parametre. Le supprimer ne touche pas aux parametres des loteries.
              </p>
            )}

            <button type="submit" className="pl-search-btn" disabled={loading}>
              {loading ? 'Recherche...' : 'Chercher'}
            </button>
          </form>

          {filter === 'Superviseur' && (
            <p className="ep-hint">
              Superviseur : liste les parametres de ses groupes et de toutes ses banques.
            </p>
          )}
        </div>
      </div>

      {error && <p className="ep-error">{error}</p>}
      {deletedMsg && <p className="ep-success">{deletedMsg}</p>}

      {configs && (
        <div className="ep-results">
          {configs.length === 0 ? (
            <p className="ep-empty">Aucun parametre trouve pour cette recherche.</p>
          ) : (
            <>
              <div className="ep-table-wrapper">
                <table className="ep-table">
                  <thead>
                    <tr>
                      <th><input type="checkbox" checked={allChecked} onChange={toggleAll} /></th>
                      <th>Loterie</th>
                      <th>Portee</th>
                      <th>Produit</th>
                      <th>Prix Points</th>
                      <th>Limite en ligne</th>
                      <th>Limite Local</th>
                      <th>Commission</th>
                    </tr>
                  </thead>
                  <tbody>
                    {configs.map((c) => (
                      <tr
                        key={c.id}
                        className={selected.includes(c.id) ? 'ep-row-selected' : ''}
                        onClick={() => toggle(c.id)}
                      >
                        <td>
                          <input
                            type="checkbox"
                            checked={selected.includes(c.id)}
                            onChange={() => toggle(c.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td>{c.tirage} - {c.tirage_name}</td>
                        <td>{scopeLabel(c)}</td>
                        <td>{c.product_abbr}</td>
                        <td>{c.points_price}</td>
                        <td>{c.online_limit}</td>
                        <td>{c.local_limit}</td>
                        <td>{c.sales_commission}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="ep-actions">
                <span className="ep-count">
                  {selected.length} / {configs.length} selectionne(s)
                </span>
                {!confirming ? (
                  <button
                    type="button"
                    className="ep-delete-btn"
                    disabled={selected.length === 0 || deleting}
                    onClick={() => setConfirming(true)}
                  >
                    🗑️ Supprimer la selection
                  </button>
                ) : (
                  <div className="ep-confirm">
                    <span>
                      Supprimer definitivement {selected.length} parametre(s) ?{' '}
                      {filter === 'General'
                        ? "Niveau general : les loteries sans parametre propre (ni TOUS) ne pourront plus vendre ces produits tant qu'un nouveau parametre n'est pas cree."
                        : "La vente reprendra le parametre TOUS de ce niveau s'il existe, sinon celui du niveau au-dessus (groupe ou company)."}
                    </span>
                    <button type="button" className="ep-delete-btn" disabled={deleting} onClick={handleDelete}>
                      {deleting ? 'Suppression...' : 'Oui, supprimer'}
                    </button>
                    <button type="button" className="ep-cancel-btn" disabled={deleting} onClick={() => setConfirming(false)}>
                      Annuler
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </Layout>
  );
};

export default EliminarParametrosPage;
